/**
 * विधानसभा की सभी जगहें in the field app (N-PAC, 31 Oct 2026: its own button at the bottom for the login
 * given this duty): every booth's markets, chowks, crowded spots and settlements, with GPS or not. A booth
 * opens its places screen to add or change one (the same screen as from the booth). Online only.
 */
import { useEffect, useMemo, useState } from "react";
import { apiErrorText } from "@core/messages";
import type { AreaPlaces } from "../field/api";
import { useApp } from "../ui/context";
import { href, navigate } from "../ui/router";
import { Banner, Button, Field } from "../ui/components";
import { MyPoints } from "./MyPoints";

const KIND_ICON: Record<string, string> = { market: "🛒", chowk: "🚦", crowd: "👥", village: "🏘️", slum: "🏚️", dalit: "🏠", tribal: "🛖", dense: "🏙️", other: "📍" };
const fmt = (n: number) => n.toLocaleString("en-IN");

export function AllPlacesScreen() {
  const { app, t, lang } = useApp();
  const [data, setData] = useState<AreaPlaces | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tries, setTries] = useState(0);
  const [q, setQ] = useState("");
  const [code, setCode] = useState("");
  const [codeBad, setCodeBad] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  // only the places still without GPS (listed from the office; the surveyor locates them on the spot)
  const [onlyNoGps, setOnlyNoGps] = useState(false);

  useEffect(() => {
    let off = false;
    setErr(null);
    app.online((api, aid) => api.areaPlaces(aid)).then(
      (d) => !off && setData(d),
      (e) => !off && setErr((e as { status?: number })?.status ? apiErrorText(e, lang) ?? String(e) : t.netNeeded)
    );
    return () => {
      off = true;
    };
  }, [app, lang, t, tries]);

  const groups = useMemo(() => {
    if (!data) return [];
    const n = q.trim().toLowerCase();
    const byBooth = new Map<string, AreaPlaces["places"]>();
    for (const p of data.places) if (!onlyNoGps || p.lat == null) byBooth.set(p.boothCode, [...(byBooth.get(p.boothCode) ?? []), p]);
    return data.booths
      .filter((b) => (onlyNoGps ? byBooth.has(b.code) : b.places > 0 || byBooth.has(b.code)))
      .map((b) => ({ b, places: byBooth.get(b.code) ?? [] }))
      .filter(({ b, places }) => !n || b.code.toLowerCase() === n || b.name.toLowerCase().includes(n) || (b.panchayat ?? "").toLowerCase().includes(n) || places.some((p) => p.name.toLowerCase().includes(n)));
  }, [data, q, onlyNoGps]);
  const noGps = data ? data.summary.places - data.summary.placesWithGps : 0;
  const empty = useMemo(() => (data ? data.booths.filter((b) => !b.places && !data.places.some((p) => p.boothCode === b.code)) : []), [data]);

  const openBooth = () => {
    const c = code.trim().toUpperCase();
    if (!data || !data.booths.some((b) => b.code === c)) return setCodeBad(true);
    navigate({ name: "places", code: c });
  };

  return (
    <div className="stack" data-testid="all-places">
      <MyPoints />
      <div>
        <h2 style={{ margin: "0 0 4px" }}>📍 {t.apTitle}</h2>
        <p className="muted" style={{ margin: 0 }}>
          {t.apExplain}
        </p>
      </div>
      {err ? (
        <Banner kind="error" action={{ label: t.reload, onClick: () => setTries((x) => x + 1) }}>
          {err}
        </Banner>
      ) : null}
      {!data && !err ? (
        <div className="opening" role="status">
          <span className="spinner" aria-hidden />
        </div>
      ) : null}
      {data ? (
        <>
          <div className="card ap-sum" data-testid="ap-summary">
            {t.apSummary(fmt(data.summary.places), fmt(data.summary.placesWithGps), fmt(data.summary.boothsWithPlaces), fmt(data.summary.booths))}
            {noGps > 0 ? (
              <label className="ap-nogps-toggle">
                <input type="checkbox" checked={onlyNoGps} onChange={(e) => setOnlyNoGps(e.target.checked)} data-testid="ap-only-nogps" /> {t.apOnlyNoGps(fmt(noGps))}
              </label>
            ) : null}
          </div>
          <div className="card">
            <div className="ap-add">
              <Field
                label={t.apAdd}
                name="ap-booth"
                value={code}
                onChange={(v) => {
                  setCode(v);
                  setCodeBad(false);
                }}
                inputMode="numeric"
                maxLength={5}
                onEnter={openBooth}
                error={codeBad ? t.apNoBooth : undefined}
              />
              <Button onClick={openBooth} disabled={!code.trim()}>
                {t.apOpen}
              </Button>
            </div>
          </div>
          <Field label={t.apSearch} name="ap-search" value={q} onChange={setQ} />
          {groups.length ? (
            groups.map(({ b, places }) => (
              <a key={b.code} href={href({ name: "places", code: b.code })} className="card card-link ap-booth" data-testid={`ap-booth-${b.code}`}>
                <span className="ap-bh">
                  <b>
                    {lang === "hi" ? "बूथ" : "Booth"} {b.code}
                  </b>{" "}
                  <small className="muted">
                    {b.name}
                    {b.panchayat ? ` · ${b.panchayat}` : ""}
                  </small>
                </span>
                <ul className="ap-list">
                  {places.map((p) => (
                    <li key={p.id} className={p.densest ? "ap-densest" : undefined}>
                      <span aria-hidden>{KIND_ICON[p.kind] ?? "📍"}</span> {p.name} <small className="muted">· {t.bsKinds[p.kind] ?? p.kind}</small>
                      {p.lat == null ? <small className="ap-nogps"> · {t.apNoGps}</small> : <small className="ap-gps"> · GPS ✓</small>}
                    </li>
                  ))}
                </ul>
                <span className="chev" aria-hidden>
                  ›
                </span>
              </a>
            ))
          ) : (
            <p className="muted">{t.apNone}</p>
          )}
          {empty.length ? (
            <div className="card">
              <button type="button" className="ap-empty-h" onClick={() => setShowEmpty((x) => !x)} aria-expanded={showEmpty}>
                {t.apEmptyBooths(fmt(empty.length))} {showEmpty ? "▴" : "▾"}
              </button>
              {showEmpty ? (
                <div className="ap-chips">
                  {empty.map((b) => (
                    <a key={b.code} className="chip" href={href({ name: "places", code: b.code })}>
                      {b.code}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
